import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, Globe, Search, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { trackEvent } from '@/lib/analytics';

const HEADER_COPY = {
  ko: {
    features: '특징', notices: '공지사항', pricing: '요금제', about: '회사 소개',
    freeTrial: '무료 체험', freeDiagnose: '무료 진단', login: '로그인', startFree: '무료 시작하기',
    dashboard: '대시보드', altGeneration: 'ALT 생성', profile: '프로필', logout: '로그아웃',
    switchTo: 'English', greeting: (name: string) => `${name}님`,
  },
  en: {
    features: 'Features', notices: 'Notices', pricing: 'Pricing', about: 'About',
    freeTrial: 'Free Trial', freeDiagnose: 'Free Diagnosis', login: 'Log In', startFree: 'Start Free',
    dashboard: 'Dashboard', altGeneration: 'Generate ALT', profile: 'Profile', logout: 'Log Out',
    switchTo: '한국어', greeting: (name: string) => `Hi, ${name}`,
  },
} as const;

export function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { language, setLanguage } = useLanguage();
  const t = HEADER_COPY[language];
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const isPublicPage = ['/login', '/signup', '/find-account', '/reset-password'].includes(location.pathname);
  const isLandingPage = location.pathname === '/';

  const trackNavClick = (item: string, target: string) => trackEvent('header_nav_click', { item, target });

  const scrollToSection = (sectionId: string) => {
    if (!isLandingPage) {
      navigate('/', { state: { scrollTo: sectionId } });
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // 로그인된 사용자는 공개 페이지에서 헤더를 보지 않음
  if (user && isPublicPage) {
    return null;
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* 로고 */}
          <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img src="/favicon-32x32.png" alt="ALT Generation Logo" className="h-8 w-8" />
            <span className="text-xl font-bold text-gray-900">ALT Generation</span>
          </Link>

          {/* 데스크탑 네비게이션 */}
          <nav className="hidden md:flex items-center gap-6">
            {!user ? (
              <>
                <button
                  onClick={() => { trackNavClick('nav_features', '#features'); scrollToSection('features'); }}
                  className="text-gray-700 hover:text-primary font-medium transition-colors"
                >
                  {t.features}
                </button>
                <button
                  onClick={() => { trackNavClick('nav_notices', '/notices'); navigate('/notices'); }}
                  className="text-gray-700 hover:text-primary font-medium transition-colors"
                >
                  {t.notices}
                </button>
                <button
                  onClick={() => { trackNavClick('nav_pricing', '#pricing'); scrollToSection('pricing'); }}
                  className="text-gray-700 hover:text-primary font-medium transition-colors"
                >
                  {t.pricing}
                </button>
                <button
                  onClick={() => { trackNavClick('nav_about', '#about'); scrollToSection('about'); }}
                  className="text-gray-700 hover:text-primary font-medium transition-colors"
                >
                  {t.about}
                </button>
                <div className="flex items-center gap-3 ml-4">
                  <button
                    onClick={() => setLanguage(language === 'ko' ? 'en' : 'ko')}
                    className="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:text-primary hover:bg-gray-100 rounded-md transition-colors"
                    title="Change language"
                  >
                    <Globe className="h-4 w-4" />
                    <span>{t.switchTo}</span>
                  </button>
                  <Button
                    variant="outline"
                    onClick={() => { trackNavClick('nav_free_trial', '#free-trial'); scrollToSection('free-trial'); }}
                    className="font-medium border-brand-blue-light text-brand-blue hover:bg-blue-50 gap-1.5"
                  >
                    <Sparkles className="h-4 w-4" />
                    {t.freeTrial}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { trackNavClick('nav_diagnose', '/diagnose'); navigate('/diagnose'); }}
                    className="font-medium border-green-500 text-green-700 hover:bg-green-50 gap-1.5"
                  >
                    <Search className="h-4 w-4" />
                    {t.freeDiagnose}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { trackNavClick('nav_login', '/login'); navigate('/login'); }}
                    className="font-medium"
                  >
                    {t.login}
                  </Button>
                  <Button
                    onClick={() => { trackNavClick('nav_signup', '/signup'); navigate('/signup'); }}
                    className="font-medium"
                  >
                    {t.startFree}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/dashboard"
                  className={`text-gray-700 hover:text-primary font-medium transition-colors ${
                    location.pathname === '/dashboard' ? 'text-primary' : ''
                  }`}
                >
                  {t.dashboard}
                </Link>
                <Link
                  to="/dashboard/crawler/website"
                  className={`text-gray-700 hover:text-primary font-medium transition-colors ${
                    location.pathname.includes('/crawler') ? 'text-primary' : ''
                  }`}
                >
                  {t.altGeneration}
                </Link>
                <Link
                  to="/dashboard/profile/edit"
                  className={`text-gray-700 hover:text-primary font-medium transition-colors ${
                    location.pathname.includes('/profile') ? 'text-primary' : ''
                  }`}
                >
                  {t.profile}
                </Link>
                <div className="flex items-center gap-3 ml-4">
                  <button
                    onClick={() => setLanguage(language === 'ko' ? 'en' : 'ko')}
                    className="flex items-center gap-1 px-2 py-1 text-sm text-gray-600 hover:text-primary hover:bg-gray-100 rounded-md transition-colors"
                    title="Change language"
                  >
                    <Globe className="h-4 w-4" />
                    <span>{t.switchTo}</span>
                  </button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/diagnose')}
                    className="font-medium border-green-500 text-green-700 hover:bg-green-50 gap-1.5"
                  >
                    <Search className="h-4 w-4" />
                    {t.freeDiagnose}
                  </Button>
                  <span className="text-sm text-gray-600">{t.greeting(user.name)}</span>
                  <Button
                    variant="outline"
                    onClick={handleLogout}
                    className="font-medium"
                  >
                    {t.logout}
                  </Button>
                </div>
              </>
            )}
          </nav>

          {/* 모바일 메뉴 버튼 */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-700 hover:text-primary transition-colors"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* 모바일 메뉴 */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t">
            <nav className="flex flex-col gap-4">
              {!user ? (
                <>
                  <button
                    onClick={() => {
                      trackNavClick('nav_features', '#features');
                      scrollToSection('features');
                      setMobileMenuOpen(false);
                    }}
                    className="text-gray-700 hover:text-primary font-medium transition-colors py-2 text-left"
                  >
                    {t.features}
                  </button>
                  <button
                    onClick={() => {
                      trackNavClick('nav_pricing', '#pricing');
                      scrollToSection('pricing');
                      setMobileMenuOpen(false);
                    }}
                    className="text-gray-700 hover:text-primary font-medium transition-colors py-2 text-left"
                  >
                    {t.pricing}
                  </button>
                  <Link
                    to="/notices"
                    className="text-gray-700 hover:text-primary font-medium transition-colors py-2"
                    onClick={() => {
                      trackNavClick('nav_notices', '/notices');
                      setMobileMenuOpen(false);
                    }}
                  >
                    {t.notices}
                  </Link>
                  <button
                    onClick={() => {
                      trackNavClick('nav_about', '#about');
                      scrollToSection('about');
                      setMobileMenuOpen(false);
                    }}
                    className="text-gray-700 hover:text-primary font-medium transition-colors py-2 text-left"
                  >
                    {t.about}
                  </button>
                  <div className="flex flex-col gap-2 pt-2 border-t">
                    <button
                      onClick={() => setLanguage(language === 'ko' ? 'en' : 'ko')}
                      className="flex items-center gap-2 py-2 text-sm text-gray-600 hover:text-primary transition-colors"
                    >
                      <Globe className="h-4 w-4" />
                      {t.switchTo}
                    </button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        trackNavClick('nav_free_trial', '#free-trial');
                        scrollToSection('free-trial');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full font-medium border-brand-blue-light text-brand-blue hover:bg-blue-50 gap-2"
                    >
                      <Sparkles className="h-4 w-4" />
                      {t.freeTrial}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        trackNavClick('nav_diagnose', '/diagnose');
                        navigate('/diagnose');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full font-medium border-green-500 text-green-700 hover:bg-green-50 gap-2"
                    >
                      <Search className="h-4 w-4" />
                      {t.freeDiagnose}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        trackNavClick('nav_login', '/login');
                        navigate('/login');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full font-medium"
                    >
                      {t.login}
                    </Button>
                    <Button
                      onClick={() => {
                        trackNavClick('nav_signup', '/signup');
                        navigate('/signup');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full font-medium"
                    >
                      {t.startFree}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Link
                    to="/dashboard"
                    className="text-gray-700 hover:text-primary font-medium transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t.dashboard}
                  </Link>
                  <Link
                    to="/dashboard/crawler/website"
                    className="text-gray-700 hover:text-primary font-medium transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t.altGeneration}
                  </Link>
                  <Link
                    to="/dashboard/profile/edit"
                    className="text-gray-700 hover:text-primary font-medium transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t.profile}
                  </Link>
                  <div className="flex flex-col gap-2 pt-2 border-t">
                    <button
                      onClick={() => setLanguage(language === 'ko' ? 'en' : 'ko')}
                      className="flex items-center gap-2 py-2 text-sm text-gray-600 hover:text-primary transition-colors"
                    >
                      <Globe className="h-4 w-4" />
                      {t.switchTo}
                    </button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        navigate('/diagnose');
                        setMobileMenuOpen(false);
                      }}
                      className="w-full font-medium border-green-500 text-green-700 hover:bg-green-50 gap-2"
                    >
                      <Search className="h-4 w-4" />
                      {t.freeDiagnose}
                    </Button>
                    <span className="text-sm text-gray-600 py-2">{t.greeting(user.name)}</span>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="w-full font-medium"
                    >
                      {t.logout}
                    </Button>
                  </div>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
